import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { MdAddBox } from "react-icons/md";
import { MdDescription, MdDelete, MdKeyboardArrowDown } from "react-icons/md";

const FilePreviewLoader = ({ file, preview, type }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [text, setText] = useState(null); // always declare hooks first

    useEffect(() => {
        if (!preview) return;

        // Image pre-load
        if (type === "image") {
            const img = new Image();
            img.src = preview;
            img.onload = () => setLoading(false);
            img.onerror = () => {
                setError(true);
                setLoading(false);
            };
        }

        // PDF or text from URL → just short delay (simulate loading)
        else if (type === "pdf" || (type === "text" && preview?.startsWith("http"))) {
            const timer = setTimeout(() => setLoading(false), 400);
            return () => clearTimeout(timer);
        }

        // Local text file → read via FileReader
        else if (type === "text" && (file instanceof File || file instanceof Blob)) {
            (async () => {
                try {
                    const t = await file.text();
                    setText(t.slice(0, 2000));
                    setLoading(false);
                } catch {
                    setError(true);
                    setLoading(false);
                }
            })();
        }

        // other unsupported types
        else {
            setLoading(false);
        }
    }, [file, preview, type]);

    // loading state
    if (loading) return <PreviewText>⏳ Loading preview...</PreviewText>;
    if (error) return <PreviewText>⚠️ Unable to load preview.</PreviewText>;

    // Render content after loading
    if (type === "image") {
        return <PreviewImage src={preview} alt={file?.name || "Image"} />;
    }

    if (type === "pdf") {
        return <PreviewPdf src={preview} title={file?.name || "PDF"} />;
    }

    if (type === "text") {
        if (text) return <PreviewText>{text}</PreviewText>;
        if (preview?.startsWith("http")) {
            return (
                <iframe
                    src={preview}
                    title="Text preview"
                    style={{
                        width: "100%",
                        height: "250px",
                        border: "none",
                        borderRadius: "6px",
                        background: "#fff",
                    }}
                />
            );
        }
    }

    return <PreviewText>⚠️ Unsupported preview type.</PreviewText>;
};

const FileUploadSection = ({ currentFiles, onFilesChange }) => {

    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);
    const started = useRef(false);
    const [expandedIndex, setExpandedIndex] = useState(null);

    const toggleExpand = (index) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files).map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));
        setFiles((prevFiles) => {
            const newFiles = selectedFiles.filter(sf =>
                !prevFiles.some(pf =>
                    pf.file.name === sf.file.name && pf.file.size === sf.file.size
                )
            );
            return [...prevFiles, ...newFiles];
        });
        event.target.value = '';
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const droppedFiles = Array.from(event.dataTransfer.files).map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));
        setFiles((prevFiles) => {
            const newFiles = droppedFiles.filter(df =>
                !prevFiles.some(pf =>
                    pf.file.name === df.file.name && pf.file.size === df.file.size
                )
            );
            return [...prevFiles, ...newFiles];
        });
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => {
            const updatedFiles = [...prevFiles];
            updatedFiles.splice(index, 1);
            return updatedFiles;
        });
    };

    const truncateFileName = (name, maxLength = 28) => {
        if (name.length <= maxLength) {
            return name;
        }
        return name.substring(0, maxLength - 3) + '...';
    };

    function guessType(url) {
        const cleanUrl = url.split('?')[0]; // strip query params
        const ext = cleanUrl.split('.').pop()?.toLowerCase() || '';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
            return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
        if (ext === 'pdf') return 'application/pdf';
        if (['txt', 'md', 'csv', 'json', 'log'].includes(ext))
            return `text/${ext === 'md' ? 'markdown' : 'plain'}`;
        return 'application/octet-stream';
    }

    useEffect(() => {
        //console.log('Current files state:', files.map(f => ({ name: f.name, size: f.size }))); //files about to upload
        onFilesChange(files.map(f => f.file));
    }, [files, onFilesChange]);

    useEffect(() => {
        if (!started.current && currentFiles && currentFiles.length > 0) {
            const mapped = currentFiles.map((f) => {
                // Case 1: already a real File or Blob (new upload)
                if (f instanceof Blob) {
                    return { file: f, preview: URL.createObjectURL(f) };
                } else if (f.url) {
                    // Already uploaded to storage
                    return {
                        file: {
                            name: f.name || 'unknown',
                            type: f.type || guessType(f.url),
                            size: f.size || 0,
                            url: f.url,
                            fileId: f.fileId || '',
                        },
                        preview: f.url // Use storage URL directly for preview
                    };
                }

                // fallback (ignore invalid entries)
                return null;
            }).filter(Boolean);

            setFiles(mapped);
            started.current = true;
        }
    }, [currentFiles]);

    // cleanup function that releases memory from any temporary
    useEffect(() => {
        return () => {
            files.forEach(f => {
                if (f.preview?.startsWith("blob:")) {
                    URL.revokeObjectURL(f.preview);
                }
            });
        };
    }, [files]);

    if (currentFiles === undefined) {
        return null;
    }



    return (
        <FileUploadAreaWrapper
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            {files.length > 0 && (
                <UploadedFilesList>
                    {files.map((f, index) => {
                        const { file, preview } = f;
                        const isImg = file.type?.startsWith("image/");
                        const isPdf = file.type === "application/pdf";
                        const isTxt = file.type?.startsWith("text/") || preview?.match(/\.(txt|md|csv|log|json)$/i);

                        return (
                            <FileItemWrapper key={index}>
                                <FileItem>
                                    <FileDetails>
                                        <MdDescription size={24} color="#6b7280" />
                                        <FileName>{truncateFileName(file.name)}</FileName>
                                    </FileDetails>

                                    <ButtonRow>
                                        {(isImg || isPdf || isTxt) ? (
                                            <ExpandButton onClick={() => toggleExpand(index)}>
                                                <ChevronIcon $open={expandedIndex === index} />
                                                {expandedIndex === index ? "Hide Preview" : "Show Preview"}
                                            </ExpandButton>
                                        ) : (
                                            <NoPreviewText>No Preview</NoPreviewText>
                                        )}
                                        <RemoveButton onClick={() => handleRemoveFile(index)}>
                                            <MdDelete size={20} />
                                        </RemoveButton>
                                    </ButtonRow>
                                </FileItem>

                                {expandedIndex === index && (
                                    <PreviewWindow>
                                        {(isImg || isPdf || isTxt) && (
                                            <FilePreviewLoader
                                                file={file}
                                                preview={preview}
                                                type={
                                                    isImg ? "image" :
                                                        isPdf ? "pdf" :
                                                            "text"
                                                }
                                            />
                                        )}
                                    </PreviewWindow>
                                )}
                            </FileItemWrapper>
                        );
                    })}
                </UploadedFilesList>
            )}
            <FileUploadArea
                onClick={handleClick}
            >
                <FileUploadContent>
                    <FileUploadIcon><MdAddBox /></FileUploadIcon>
                    <FileUploadText>
                        Drag and drop or <span className="browse-link">browse</span> your files
                    </FileUploadText>
                    <FileInput
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileChange}
                    />
                </FileUploadContent>
            </FileUploadArea>
        </FileUploadAreaWrapper>
    );
};

const FileUploadArea = styled.div`
  border-radius: 0.75rem;
  border: 2px dashed #d1d5db;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 2.5rem 1.5rem;
  width: 100%;
  color: #6b7280;
  min-height: 160px;

  @media (max-width: 991px) {
        max-width: 100%;
    }
`;

const FileUploadAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 8px;
  width: 100%;

  @media (max-width: 991px) {
        max-width: 100%;
    }
`;

const FileUploadContent = styled.div`
  display: flex;
  width: 230px;
  max-width: 100%;
  flex-direction: column;
  align-items: center;
`;

const FileUploadIcon = styled.div`
  font-size: 3rem;
  color: #9ca3af;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 991px) {
    white-space: initial;
  }
`;

const FileUploadText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;

  .browse-link {
    font-weight: 500;
    color: #534af5;
    cursor: pointer;

    &:hover {
      color: #4338ca;
    }
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadedFilesList = styled.div`
    width: 100%;
    z-index: 2;
    margin-bottom: 12px;
    gap: 0.75rem;
    flex-direction: column;
    display: flex;
`;


const RemoveButton = styled.button.attrs({ type: 'button' })`
  margin-left: 10px;
  color: #ff4c4c;
  border: none;
  font-weight: 600;
  border-radius: 5px;
  background: none; 
  padding: 5px 10px;
  cursor: pointer;
  transition: color 0.2s;
  display: flex;
  gap: 6px;

  &:hover {
    color:rgb(195, 3, 3);
  }
`;

const FileItem = styled.div`
  font-size: 0.875rem;
  background-color:rgb(255, 255, 255);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FileDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FileName = styled.p`
  color: #1f2937;
  font-weight: 500;
`;

const FileItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ExpandButton = styled.button.attrs({ type: 'button' })`
  background: #f3f4f6;
  border: none;
  border-radius: 0.4rem;
  align-items: center;
  display: flex;
  padding: 0.45rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  color: #534af5;
  transition: background 0.2s;

  &:hover {
    background: #e5e7eb;
  }
`;

const PreviewWindow = styled.div`
  margin-top: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
  background: #fafafa;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 0.25rem;
  transition: opacity 0.3s ease;
  opacity: 1;
`;

const PreviewPdf = styled.iframe`
  width: 100%;
  height: 280px;
  border: none;
  border-radius: 0.25rem;
  transition: opacity 0.3s ease;
  opacity: 1;
`;

const PreviewText = styled.pre`
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 0.85rem;
  color: #1f2937;
  background: #ffffff;
  padding: 0.5rem;
  border-radius: 0.25rem;
  max-height: 250px;
  overflow-y: auto;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const NoPreviewText = styled.span`
  font-size: 0.8rem;
  color: #4b5563;
  font-weight: 600;
`;

const ChevronIcon = styled(MdKeyboardArrowDown).withConfig({
    shouldForwardProp: (p) => p !== '$open',
})`
  margin-left: -4px;
  margin-right: 4px;
  font-size: 24px;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;


export default FileUploadSection;
